import { safeSetHTML } from './security.js';

/**
 * Gamification Service
 * Manages user progress, XP, levels, stars, and persistence
 */

const GamificationService = {
    // Default progress structure
    defaultProgress: {
        level: 1,
        xp: 0,
        stars: 0,
        problemsCompleted: 0,
        problemsAttempted: 0,
        streak: 0,
        lastPlayed: null,
        badges: [],
        unlockedContexts: [],
        stats: {
            suma: { attempted: 0, correct: 0 },
            resta: { attempted: 0, correct: 0 },
            combinada: { attempted: 0, correct: 0 }
        },
        medals: [], // [NEW] Store earned medals from island games
        sessionStats: {
            problemsThisSession: 0,
            xpGainedThisSession: 0,
            startTime: null
        },
        sessionHistory: [] // [NEW] Store last X sessions for charts
    },

    currentProgress: null,
    storageKey: 'detectiveMath_progress',
    ensureProgress() {
        if (!this.currentProgress) {
            this.loadProgress();
        }
    },
    problemTimers: {}, // [NEW] Runtime only: problemId -> startTime

    /**
     * Initialize gamification system
     */
    init() {
        this.loadProgress();
        this.updateStreak();
        this.startSession();
        console.log('🎮 Gamification Service initialized');
    },

    /**
     * Start tracking time for a problem
     */
    startProblem(problemId) {
        this.problemTimers[problemId] = Date.now();
        console.log(`⏱️ Start timing problem: ${problemId}`);

        // System Telemetry
        if (window.logMetric) {
            window.logMetric('pedagogy', 'problem_start', { problemId });
        }
    },

    /**
     * End tracking time for a problem and record it
     */
    endProblem(problemId, isCorrect, type, level) {
        const startTime = this.problemTimers[problemId];
        if (!startTime) return 0;

        const duration = (Date.now() - startTime) / 1000; // in seconds
        delete this.problemTimers[problemId];

        console.log(`⏱️ End problem ${problemId}: ${duration}s | Correct: ${isCorrect}`);

        // System Telemetry
        if (window.logMetric) {
            window.logMetric('pedagogy', 'problem_solve', {
                problemId,
                duration,
                isCorrect,
                type,
                level
            });
        }

        // Record in history if correct
        if (isCorrect) {
            this.recordProblemMetric(type, level, duration);
        }

        return duration;
    },

    /**
     * Record a metric for a completed problem
     */
    recordProblemMetric(type, level, duration) {
        this.ensureProgress();
        if (!this.currentProgress.sessionHistory) {
            this.currentProgress.sessionHistory = [];
        }

        const entry = {
            timestamp: new Date().toISOString(),
            type,
            level,
            duration: Math.round(duration)
        };

        this.currentProgress.sessionHistory.push(entry);

        // Keep only last 50 entries to avoid localStorage bloat
        if (this.currentProgress.sessionHistory.length > 50) {
            this.currentProgress.sessionHistory.shift();
        }

        this.saveProgress();
    },

    /**
     * Load progress from localStorage or create new
     */
    loadProgress() {
        try {
            // Use UserManager to load data
            const saved = window.UserManager ? window.UserManager.loadData(this.storageKey) : null;
            if (saved) {
                this.currentProgress = {
                    ...this.defaultProgress,
                    ...saved,
                    sessionStats: { ...this.defaultProgress.sessionStats, ...(saved.sessionStats || {}) },
                    stats: { ...this.defaultProgress.stats, ...(saved.stats || {}) }
                };
            } else {
                this.currentProgress = { ...this.defaultProgress };
                this.saveProgress();
            }
        } catch (error) {
            console.error('❌ Error loading progress:', error);
            this.currentProgress = { ...this.defaultProgress };
        }
    },

    /**
     * Save progress to localStorage
     */
    saveProgress() {
        try {
            if (window.UserManager) {
                window.UserManager.saveData(this.storageKey, this.currentProgress);
            }
            console.log('💾 Progress saved');
        } catch (error) {
            console.error('❌ Error saving progress:', error);
        }
    },

    /**
     * Get current progress
     */
    getProgress() {
        this.ensureProgress();
        return this.currentProgress;
    },

    /**
     * Get progress for a specific user ID (used by Dashboard)
     */
    getUserProgress(userId) {
        if (!window.UserManager) return { ...this.defaultProgress };

        const saved = window.UserManager.loadUserData(userId, this.storageKey);
        if (saved) {
            return { ...this.defaultProgress, ...saved };
        }
        return { ...this.defaultProgress };
    },

    /**
     * Add XP and check for level up
     */
    addXP(amount, reason = '') {
        if (!window.XP_SYSTEM) {
            console.warn('⚠️ XP_SYSTEM not loaded');
            return;
        }

        this.ensureProgress();
        const oldLevel = XP_SYSTEM.getLevelInfo(this.currentProgress.xp).level;
        this.currentProgress.xp += amount;
        this.currentProgress.sessionStats.xpGainedThisSession += amount;

        const newLevelInfo = XP_SYSTEM.getLevelInfo(this.currentProgress.xp);
        const newLevel = newLevelInfo.level;

        console.log(`✨ +${amount} XP ${reason ? `(${reason})` : ''} | Total: ${this.currentProgress.xp}`);

        // Check for level up
        if (newLevel > oldLevel) {
            this.onLevelUp(newLevel, newLevelInfo.title);
        }

        this.saveProgress();
        this.updateUI();

        // Trigger custom event
        document.dispatchEvent(new CustomEvent('gamification:xpGained', {
            detail: { amount, total: this.currentProgress.xp, reason }
        }));
    },

    /**
     * Add a star
     */
    addStar() {
        this.ensureProgress();
        this.currentProgress.stars++;
        this.saveProgress();
        this.updateUI();

        console.log(`⭐ Star earned! Total: ${this.currentProgress.stars}`);
    },

    /**
     * Mark problem as completed
     */
    completeProblem(problemType, failCount = 0) {
        this.ensureProgress();
        this.currentProgress.problemsCompleted++;
        this.currentProgress.problemsAttempted++;
        this.currentProgress.sessionStats.problemsThisSession++;

        // Update stats
        if (this.currentProgress.stats[problemType]) {
            this.currentProgress.stats[problemType].attempted++;
            this.currentProgress.stats[problemType].correct++;
        }

        // Calculate XP
        let xp = XP_SYSTEM.rewards.problemComplete;

        // Bonus for perfect (no fails)
        if (failCount === 0) {
            xp += XP_SYSTEM.rewards.perfectProblem;
            this.addXP(xp, 'problema perfecto');
        } else {
            this.addXP(xp, 'problema completado');
        }

        // Add star
        this.addStar();

        // Check for badges
        if (window.BadgeSystem) {
            BadgeSystem.checkBadges();
        }

        this.saveProgress();
    },

    /**
     * Record a step correct
     */
    stepCorrect() {
        this.addXP(XP_SYSTEM.rewards.stepCorrect, 'paso correcto');
    },

    /**
     * Handle level up
     */
    onLevelUp(newLevel, newTitle) {
        this.ensureProgress();
        console.log(`🎉 ¡SUBISTE DE NIVEL! Nivel ${newLevel}: ${newTitle}`);

        // Update level in progress
        this.currentProgress.level = newLevel;

        // Play level up sound
        if (window.SoundService) {
            SoundService.playSuccess();
        }

        // Show level up modal
        this.showLevelUpModal(newLevel, newTitle);

        // Trigger confetti
        if (window.confetti) {
            confetti({
                particleCount: 150,
                spread: 100,
                origin: { y: 0.5 }
            });
        }

        // Unlock new island in adventure map
        if (window.AdventureMap) {
            window.AdventureMap.unlockNextIsland();
        }

        // [NEW] Trigger Curricular Gamification Reward
        if (window.CurricularGamification) {
            CurricularGamification.showIslandReward(newLevel);
        }

        // Check for badges
        if (window.BadgeSystem) {
            BadgeSystem.checkBadges();
        }
    },

    /**
     * Show level up modal
     */
    showLevelUpModal(level, title) {
        const modal = document.createElement('div');
        modal.className = 'level-up-modal';
        safeSetHTML(modal, `
            <div class="level-up-content">
                <div class="level-up-icon">🎉</div>
                <h2>¡SUBISTE DE NIVEL!</h2>
                <div class="level-badge">
                    <span class="level-number">${level}</span>
                </div>
                <p class="level-title">${title}</p>
                <button class="btn btn-primary" onclick="this.closest('.level-up-modal').remove()">
                    ¡Genial!
                </button>
            </div>
        `);

        document.body.appendChild(modal);

        setTimeout(() => modal.classList.add('active'), 10);

        // Auto-remove after 5 seconds if not clicked
        setTimeout(() => {
            if (modal.parentElement) {
                modal.remove();
            }
        }, 5000);
    },

    /**
     * Update streak (call once per day)
     */
    updateStreak() {
        this.ensureProgress();
        const today = new Date().toDateString();
        const lastPlayed = this.currentProgress.lastPlayed;

        if (lastPlayed) {
            const lastDate = new Date(lastPlayed);
            const todayDate = new Date(today);
            const diffDays = Math.floor((todayDate - lastDate) / (1000 * 60 * 60 * 24));

            if (diffDays === 0) {
                // Same day, do nothing
                return;
            } else if (diffDays === 1) {
                // Next day, increment streak
                this.currentProgress.streak++;
                this.addXP(XP_SYSTEM.rewards.dailyLogin, 'racha diaria');
                console.log(`🔥 Racha de ${this.currentProgress.streak} días!`);
            } else {
                // Streak broken
                console.log('💔 Racha rota');
                this.currentProgress.streak = 1;
            }
        } else {
            // First time playing
            this.currentProgress.streak = 1;
        }

        this.currentProgress.lastPlayed = today;
        this.saveProgress();
    },

    /**
     * [NEW] Award bonus for correct answer streak within a session
     */
    awardStreakBonus(streakCount) {
        if (streakCount > 0 && streakCount % 5 === 0) {
            const bonus = 20;
            this.addXP(bonus, '🔥 Racha de Aciertos');

            // Audio Feedback
            if (window.AudioService) {
                AudioService.play('success');
            }

            console.log(`🔥 Streak Bonus: ${bonus} XP for ${streakCount} correct answers`);
        }
    },

    /**
     * Start a new session
     */
    startSession() {
        this.ensureProgress();
        this.currentProgress.sessionStats = {
            problemsThisSession: 0,
            xpGainedThisSession: 0,
            startTime: new Date().toISOString()
        };
        this.saveProgress();
    },

    /**
     * Update UI elements
     */
    updateUI() {
        if (!window.XP_SYSTEM) return;
        this.ensureProgress();

        const info = XP_SYSTEM.getLevelInfo(this.currentProgress.xp);

        // Update level display
        const levelDisplay = document.getElementById('userLevel');
        if (levelDisplay) {
            levelDisplay.textContent = `Nivel ${info.level}`;
        }

        // Update XP bar
        const xpFill = document.getElementById('xpBarFill');
        const xpText = document.getElementById('xpText');

        if (xpFill && xpText) {
            const percentage = XP_SYSTEM.getProgressPercentage(this.currentProgress.xp);
            xpFill.style.width = `${percentage}%`;

            if (info.isMaxLevel) {
                xpText.textContent = `MAX (${info.currentXP} XP)`;
            } else {
                xpText.textContent = `${info.xpInLevel} / ${info.nextLevelXP - info.levelXP} XP`;
            }
        }

        // Update stars
        const starsDisplay = document.getElementById('userStars');
        if (starsDisplay) {
            starsDisplay.textContent = this.currentProgress.stars;
        }
    },

    /**
     * Reset all progress (for testing)
     */
    resetProgress() {
        if (confirm('¿Seguro que quieres reiniciar todo el progreso?')) {
            this.ensureProgress();
            this.currentProgress = { ...this.defaultProgress };
            this.saveProgress();
            this.updateUI();
            console.log('🔄 Progreso reiniciado');
            window.location.reload();
        }
    },

    /**
     * Export progress as JSON
     */
    exportProgress() {
        this.ensureProgress();
        const json = JSON.stringify(this.currentProgress, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `detective-math-progress-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
};

// Expose globally
window.GamificationService = GamificationService;

// Auto-initialize when DOM is ready
// Auto-init removed. It will be initialized by the Login Flow.
// if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', () => window.GamificationService.init());
// } else {
//     window.GamificationService.init();
// }

console.log('🎮 gamificationService.js loaded');
